import React, { useEffect, useRef, type ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { type LucideIcon } from 'lucide-react';

import { icons } from '@/lib/iconography';
import { cn } from '@/lib/utils';

type Variant = 'empty' | 'error' | 'forbidden';
type ErrorKind = 'network' | 'shape-mismatch' | 'default';

interface StateCardProps {
  variant: Variant;
  headline: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
  secondary?: ReactNode;
  size?: 'sm' | 'md';
  errorKind?: ErrorKind;
  className?: string;
}

const cardVariants = cva(
  'flex w-full flex-col items-center justify-center rounded-xl border text-center',
  {
    variants: {
      variant: {
        empty: 'border-border bg-surface text-foreground',
        error: 'border-error-300/60 bg-error-50 text-error-700',
        forbidden: 'border-border bg-surface text-foreground',
      },
      size: {
        sm: 'min-h-[160px] gap-2 p-4',
        md: 'min-h-[360px] gap-3 p-8',
      },
    },
    defaultVariants: {
      variant: 'empty',
      size: 'md',
    },
  },
);

const iconColor: Record<Variant, string> = {
  empty: 'text-muted-foreground',
  error: 'text-error-500',
  forbidden: 'text-accent-ink',
};

const errorCopy: Record<ErrorKind, string> = {
  network: "We couldn't reach the server. Check your connection and retry.",
  'shape-mismatch': 'Something looks wrong with the data we received. The team has been notified.',
  default: 'Something went wrong. Try again in a moment.',
};

/** Stable module-level helper — avoids react/no-unstable-nested-components. */
function resolveIcon(variant: Variant, icon?: LucideIcon): LucideIcon | null {
  if (icon) return icon;
  if (variant === 'error') return icons.state.error;
  if (variant === 'forbidden') return icons.state.forbidden;
  return null;
}

/**
 * Shared shell for empty, error, and forbidden non-data states across the
 * clinical surface. Never carries the BrandSwirl or brand glyph — those are
 * reserved for auth and hero moments.
 */
export function StateCard({
  variant,
  headline,
  description,
  icon,
  action,
  secondary,
  size = 'md',
  errorKind = 'default',
  className,
}: StateCardProps) {
  const actionWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (variant !== 'error' && variant !== 'forbidden') return;
    const firstFocusable = actionWrapRef.current?.querySelector<HTMLElement>(
      'button, a, [tabindex]:not([tabindex="-1"])',
    );
    firstFocusable?.focus();
  }, [variant]);

  const iconSlot = resolveIcon(variant, icon);

  const resolvedDescription =
    description ?? (variant === 'error' ? errorCopy[errorKind] : undefined);

  const hasActions = action != null || secondary != null;

  return (
    <div
      data-size={size}
      role={variant === 'error' ? 'alert' : undefined}
      className={cn(cardVariants({ variant, size }), className)}
    >
      {iconSlot !== null
        ? React.createElement(iconSlot, {
            'aria-hidden': 'true',
            className: cn('h-8 w-8', iconColor[variant]),
            strokeWidth: 1.5,
          })
        : null}
      <h2 className="text-title-md">{headline}</h2>
      {resolvedDescription != null ? (
        <p className="text-body-sm text-muted-foreground max-w-md">{resolvedDescription}</p>
      ) : null}
      {hasActions ? (
        <div ref={actionWrapRef} className="mt-2 flex items-center gap-3">
          {action}
          {secondary}
        </div>
      ) : null}
    </div>
  );
}
